import axios, {AxiosInstance} from 'axios'
import db from 'services/firebase'
import {addDoc, collection, doc, getDocs, query, serverTimestamp, where} from '@firebase/firestore'
import {
  dismissLoadingToast,
  showErrorToast,
  showLoadingToast,
  showSuccessToast,
  updateLoadingToast,
} from 'app/components/ShowToast'

interface BitbucketWorkspace {
  name?: string
  slug?: string
  uuid?: string
  links?: {
    html?: {
      href: string
    }
  }
  type?: string
}

interface Project {
  name: string
  html_url: string
  private: boolean
  description?: string | null
  created_on?: string
  updated_on: string
  last_activity_at: string
  stargazers_count: number
  forks_count: number
  open_issues_count: number
  watchers_count: number
  language?: string | null
  default_branch: string
  is_repo: boolean
  repo_name: string
  web_url: string
  platform: string
  visibility: string
  size?: number
  star_count: number
  watchers?: number
  forks: number
  open_issues: number
  workspace?: BitbucketWorkspace
  owner?: {
    login: string
    uuid: string
    html_url: string
    type: string
  }
  tenantId?: string
  uid?: string
  createdAt?: any
}

interface BitbucketIntegrationResponse {
  success: boolean
  repositories?: Project[]
  error?: string
}

interface CheckRepositoriesResponse {
  success: boolean
  newRepositories?: Project[]
  error?: string
}

interface BitbucketError {
  response?: {
    status?: number
    data?: {
      error?: {
        message?: string
      }
      message?: string
    }
  }
  message?: string
}

interface BitbucketMember {
  user: {
    uuid: string
    display_name: string
    nickname: string
    account_id: string
    type: string
    role: string
  }
  workspace: {
    slug: string
    name: string
    type: string
  }
  links: {
    self: {
      href: string
    }
  }
  type: string
}

interface BitbucketRepository {
  slug: string
  name: string
  description: string
  website: string | null
  is_private: boolean
  uuid: string
  created_on: string
  updated_on: string
  owner: {
    display_name: string
    links: {
      self: {
        href: string
      }
      html: {
        href: string
      }
    }
    account_id: string
    nickname: string
    type: string
    uuid: string
  }
  workspace: {
    slug: string
    name: string
    type: string
    links: {
      self: {
        href: string
      }
      html: {
        href: string
      }
    }
    uuid: string
  }
  mainbranch: {
    name: string
    type: string
  }
  links: {
    self: {
      href: string
    }
    html: {
      href: string
    }
    avatar: {
      href: string
    }
  }
  language: string
  size: number
  project: {
    links: {
      self: {
        href: string
      }
      html: {
        href: string
      }
    }
    name: string
    key: string
    type: string
    uuid: string
  }
}

export const createBitbucketIntegration = ({
  accessToken,
  workspaceId,
  baseUrl = 'https://api.bitbucket.org/2.0',
}: {
  accessToken: string
  workspaceId: string
  baseUrl?: string
}) => {
  const axiosInstance: AxiosInstance = axios.create({
    baseURL: baseUrl,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  })

  const getUserFriendlyErrorMessage = (error: unknown): string => {
    const bitbucketError = error as BitbucketError
    if (bitbucketError.response?.status === 403) {
      return 'Access forbidden. Please check your access token permissions.'
    } else if (bitbucketError.response?.status === 401) {
      return 'Invalid access token. Please reconnect to Bitbucket.'
    } else if (bitbucketError.response?.status === 404) {
      return 'Bitbucket workspace not found. Please check the workspace ID.'
    } else if (bitbucketError.response?.status === 429) {
      return 'Bitbucket API rate limit exceeded. Please try again later.'
    }
    return (
      bitbucketError.response?.data?.error?.message ||
      bitbucketError.response?.data?.message ||
      bitbucketError.message ||
      'An unexpected error occurred.'
    )
  }

  const getWorkspaceSlug = (workspace: string): string => {
    return encodeURIComponent(workspace.toLowerCase())
  }

  const checkMFAStatus = async () => {
    try {
      const workspaceSlug = getWorkspaceSlug(workspaceId)
      const response = await axiosInstance.get(`/workspaces/${workspaceSlug}/members`)
      const members = response.data.values

      return {
        success: true,
        message: `Retrieved ${members.length} members. Note: MFA status cannot be directly checked via Bitbucket API.`,
        members: members.map((member: BitbucketMember) => ({
          username: member.user.display_name,
          accountId: member.user.account_id,
          uuid: member.user.uuid,
        })),
      }
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error)
      showErrorToast({message: errorMessage})
      return {success: false, error: errorMessage}
    }
  }

  const getWorkspaceMembers = async () => {
    try {
      const workspaceSlug = getWorkspaceSlug(workspaceId)
      let allMembers: BitbucketMember[] = []
      let url = `/workspaces/${workspaceSlug}/members`

      while (url) {
        const response = await axiosInstance.get(url)
        const members = response.data.values
        allMembers = [...allMembers, ...members]

        url = response.data.next
          ? response.data.next.replace(axiosInstance.defaults.baseURL as string, '')
          : null
      }
      const mappedMembers = allMembers.map((member: BitbucketMember) => ({
        username: member.user.display_name,
        accountId: member.user.account_id,
        uuid: member.user.uuid,
        memberType: member.user.type,
        nickname: member.user.nickname,
        accountRole: member.user.role || '', 
        workspaceName: member.workspace.name,
        workspace: {
          slug: member.workspace.slug,
          name: member.workspace.name,
          type: member.workspace.type,
        },
      }))

      return {
        success: true,
        members: mappedMembers,
        message: `Retrieved ${mappedMembers.length} workspace members`,
      }
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error)
      showErrorToast({message: errorMessage})
      return {success: false, error: errorMessage}
    }
  }

  const checkBranchProtection = async (repoSlug: string) => {
    try {
      const repoResponse = await axiosInstance.get(`/repositories/${workspaceId}/${repoSlug}`)
      const defaultBranch = repoResponse.data.mainbranch?.name || 'master'

      const branchRestrictionsResponse = await axiosInstance.get(
        `/repositories/${workspaceId}/${repoSlug}/branch-restrictions`
      )

      const branchRestrictions = branchRestrictionsResponse.data.values

      const defaultBranchProtections = branchRestrictions.filter(
        (restriction: any) => restriction.pattern === defaultBranch
      )

      return {
        success: true,
        defaultBranch,
        isProtected: defaultBranchProtections.length > 0,
        protections: defaultBranchProtections,
      }
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error)
      return {success: false, error: errorMessage}
    }
  }

  const checkCodeReviewRequirements = async (repoSlug: string) => {
    try {
      const defaultBranchResponse = await checkBranchProtection(repoSlug)
      if (!defaultBranchResponse.success) {
        return {success: false, error: defaultBranchResponse.error}
      }

      const defaultBranch = defaultBranchResponse.defaultBranch
      const hasReviewRequirement = defaultBranchResponse.protections.some(
        (protection: any) => protection.kind === 'require_approvals_to_merge'
      )

      return {
        success: true,
        defaultBranch,
        requiresReviews: hasReviewRequirement,
      }
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error)
      return {success: false, error: errorMessage}
    }
  }

  const checkVulnerabilities = async (repoSlug: string) => {
    try {
      return {
        success: true,
        message:
          "Bitbucket doesn't provide direct vulnerability scanning via API. Consider integrating with a third-party security scanner.",
        vulnerabilities: [],
      }
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error)
      return {success: false, error: errorMessage}
    }
  }

  const checkOffboardedUserAccess = async (offboardedUserIds: string[]) => {
    try {
      const workspaceSlug = getWorkspaceSlug(workspaceId)
      const response = await axiosInstance.get(`/workspaces/${workspaceSlug}/members`)
      const members = response.data.values

      const offboardedMembers = members.filter((member: BitbucketMember) =>
        offboardedUserIds.includes(member.user.account_id)
      )

      return {
        success: true,
        offboardedUsersWithAccess: offboardedMembers.map((member: BitbucketMember) => ({
          username: member.user.display_name,
          accountId: member.user.account_id,
        })),
      }
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error)
      return {success: false, error: errorMessage}
    }
  }

  const getAllRepositories = async (): Promise<BitbucketIntegrationResponse> => {
    try {
      const workspaceSlug = getWorkspaceSlug(workspaceId)
      let allRepositories: BitbucketRepository[] = []
      let url = `/repositories/${workspaceSlug}`

      while (url) {
        const response = await axiosInstance.get(url)
        const repositories = response.data.values
        allRepositories = [...allRepositories, ...repositories]

        url = response.data.next
          ? response.data.next.replace(axiosInstance.defaults.baseURL as string, '')
          : null
      }

      const mappedRepositories = allRepositories.map((repo: BitbucketRepository) => ({
        name: repo.name,
        html_url: repo.links.html.href,
        private: repo.is_private,
        description: repo.description,
        created_on: repo.created_on,
        updated_on: repo.updated_on,
        last_activity_at: repo.updated_on,
        stargazers_count: 0,
        forks_count: 0,
        open_issues_count: 0,
        watchers_count: 0,
        language: repo.language,
        default_branch: repo.mainbranch?.name,
        is_repo: true,
        repo_name: repo.name,
        web_url: repo.links.html.href,
        platform: 'Bitbucket',
        visibility: repo.is_private ? 'private' : 'public',
        size: repo.size,
        star_count: 0,
        watchers: 0,
        forks: 0,
        open_issues: 0,
        workspace: {
          name: repo.workspace.name,
          slug: repo.workspace.slug,
          uuid: repo.workspace.uuid,
          links: repo.workspace.links,
          type: repo.workspace.type,
        },
        owner: {
          login: repo.owner.display_name,
          uuid: repo.owner.uuid,
          html_url: repo.owner.links.html.href,
          type: repo.owner.type,
        },
      }))

      return {success: true, repositories: mappedRepositories}
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error)
      return {success: false, error: errorMessage}
    }
  }

  const checkForNewRepositories = async (
    existingRepositories: Project[] = [],
    lastCheckTimestamp?: string
  ): Promise<CheckRepositoriesResponse> => {
    try {
      let allRepositories: BitbucketRepository[] = []
      let url = `/repositories/${workspaceId}`

      if (lastCheckTimestamp) {
        url += `?q=updated_on > ${lastCheckTimestamp}`
      }

      while (url) {
        const response = await axiosInstance.get(url)
        const repositories = response.data.values
        allRepositories = [...allRepositories, ...repositories]

        url = response.data.next
          ? response.data.next.replace(axiosInstance.defaults.baseURL as string, '')
          : null
      }

      const existingRepoUrls = new Set(existingRepositories.map((repo) => repo?.web_url))

      const newRepositories = allRepositories.filter(
        (repo) => !existingRepoUrls.has(repo.links.html.href)
      )

      const mappedNewRepositories = newRepositories.map((repo: BitbucketRepository) => ({
        name: repo.name,
        html_url: repo.links.html.href,
        private: repo.is_private,
        description: repo.description,
        created_on: repo.created_on,
        updated_on: repo.updated_on,
        last_activity_at: repo.updated_on,
        stargazers_count: 0,
        forks_count: 0,
        open_issues_count: 0,
        watchers_count: 0,
        language: repo.language,
        default_branch: repo.mainbranch?.name,
        is_repo: true,
        repo_name: repo.name,
        web_url: repo.links.html.href,
        platform: 'Bitbucket',
        visibility: repo.is_private ? 'private' : 'public',
        size: repo.size,
        star_count: 0,
        watchers: 0,
        forks: 0,
        open_issues: 0,
        workspace: {
          name: repo.workspace.name,
          slug: repo.workspace.slug,
          uuid: repo.workspace.uuid,
          links: repo.workspace.links,
          type: repo.workspace.type,
        },
        owner: {
          login: repo.owner.display_name,
          uuid: repo.owner.uuid,
          html_url: repo.owner.links.html.href,
          type: repo.owner.type,
        },
      }))

      return {success: true, newRepositories: mappedNewRepositories}
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error)
      return {success: false, error: errorMessage}
    }
  }

  const getRepositoryDetails = async (repoSlug: string) => {
    try {
      const response = await axiosInstance.get(`/repositories/${workspaceId}/${repoSlug}`)
      const repo = response.data

      return {
        success: true,
        repository: {
          name: repo.name,
          html_url: repo.links.html.href,
          private: repo.is_private,
          description: repo.description,
          created_on: repo.created_on,
          updated_on: repo.updated_on,
          last_activity_at: repo.updated_on,
          stargazers_count: 0,
          forks_count: 0,
          open_issues_count: 0,
          watchers_count: 0,
          language: repo.language,
          default_branch: repo.mainbranch?.name,
          is_repo: true,
          repo_name: repo.name,
          web_url: repo.links.html.href,
          platform: 'Bitbucket',
          visibility: repo.is_private ? 'private' : 'public',
          size: repo.size,
          star_count: 0,
          watchers: 0,
          forks: 0,
          open_issues: 0,
          workspace: {
            name: repo.workspace.name,
            slug: repo.workspace.slug,
            uuid: repo.workspace.uuid,
            links: repo.workspace.links,
            type: repo.workspace.type,
          },
          owner: {
            login: repo.owner.display_name,
            uuid: repo.owner.uuid,
            html_url: repo.owner.links.html.href,
            type: repo.owner.type,
          },
        },
      }
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error)
      return {success: false, error: errorMessage}
    }
  }

  const runChecks = async (repoSlug: string) => {
    const results = {
      mfaStatus: await checkMFAStatus(),
      branchProtection: await checkBranchProtection(repoSlug),
      codeReviewRequirements: await checkCodeReviewRequirements(repoSlug),
      vulnerabilities: await checkVulnerabilities(repoSlug),
    }
    return results
  }

  const runAllChecks = async () => {
    try {
      const repositories = await getAllRepositories()
      if (!repositories.success || !repositories.repositories) {
        return {success: false, error: repositories.error || 'Failed to fetch repositories'}
      }

      const results = await Promise.all(
        repositories.repositories.map(async (repo) => {
          const repoSlug = repo.repo_name
          const checks = await runChecks(repoSlug)
          return {
            repository: repo,
            checks,
          }
        })
      )

      return {success: true, results}
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error)
      return {success: false, error: errorMessage}
    }
  }

  return {
    runChecks,
    runAllChecks,
    getAllRepositories,
    getRepositoryDetails,
    checkMFAStatus,
    checkBranchProtection,
    checkCodeReviewRequirements,
    checkVulnerabilities,
    checkOffboardedUserAccess,
    checkForNewRepositories,
    getWorkspaceMembers,
  }
}

export const exchangeCodeForBitbucketToken = async (
  code: string,
  currentUser: any,
  navigate: (path: string, options?: {replace: boolean}) => void
) => {
  let toastId
  try {
    // Step 1: Exchange code for token
    toastId = showLoadingToast({message: 'Authenticating with Bitbucket...'})
    const tokenResponse = await fetch(
      `https://us-central1-slate-134a0.cloudfunctions.net/bitbucketOAuthCallback?code=${encodeURIComponent(
        code
      )}`,
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          Accept: 'application/json',
        },
      }
    )

    const responseText = await tokenResponse.text()

    let data
    try {
      data = JSON.parse(responseText)
    } catch (e) {
      throw new Error('Invalid response format from server')
    }

    if (
      !data.access_token ||
      typeof data.access_token !== 'string' ||
      data.access_token.trim() === ''
    ) {
      throw new Error('No valid access token received from server')
    }

    updateLoadingToast(toastId, {
      message: 'Token received, fetching repositories...',
      type: 'info',
      isLoading: true,
    })

    localStorage.setItem('bitbucket_access_token', data.access_token)

    const integrationId = await fetchAndSaveBitbucketRepositories(
      data.access_token,
      data.workspaces,
      currentUser,
      navigate
    );
    dismissLoadingToast(toastId)
    showSuccessToast({message: 'Bitbucket authentication successful'})

    navigate(`/integrations?id=${integrationId}&service=Bitbucket`, { replace: true });
  } catch (error: any) {
    showErrorToast({message: 'Bitbucket authentication failed'})
    dismissLoadingToast(toastId)
    localStorage.removeItem('bitbucket_access_token')
    navigate('/integrations', {replace: true})
    throw error
  }
}

export const fetchAndSaveBitbucketRepositories = async (
  accessToken: string,
  workspaces: any[],
  currentUser: any,
  navigate: (path: string, options?: { replace: boolean }) => void
): Promise<string | undefined> => {
  try {
    let integrationId: string | undefined;
    if (currentUser?.tenantId) {
      const tenantRef = doc(db, 'tenants', currentUser.tenantId);

      const integrationsRef = collection(tenantRef, 'integrations');
      const integrationQuery = query(integrationsRef, where('name', '==', 'Bitbucket'));
      const existingIntegration = await getDocs(integrationQuery);

      if (existingIntegration.empty) {
        const currentDate = new Date();
        const formattedDate = `${currentDate.getFullYear()}-${String(
          currentDate.getMonth() + 1
        ).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;

        const docRef = await addDoc(integrationsRef, {
          accessToken,
          name: 'Bitbucket',
          group: 'Developer Tools',
          img_path: '/integrations/Bitbucket.svg',
          status: 'Connected',
          connectedAt: formattedDate,
          tenantId: currentUser.tenantId,
          uid: currentUser.uid,
          workspaces,
        });
        integrationId = docRef.id;

        const vendorsRef = collection(tenantRef, 'vendors');
        await addDoc(vendorsRef, {
          authentication_type: '',
          category: 'Developer Tools',
          created_at: serverTimestamp(),
          due_deligence_status: 'Incomplete',
          impact_assessment: [],
          logo: [
            {
              name: 'bitbucket_logo.png',
              size: 3395,
              url: '/integrations/Bitbucket.svg',
            },
          ],
          owner: currentUser.userName || '',
          policy_url: 'https://www.atlassian.com/legal/security',
          reliance: '',
          risk_level: '',
          tenantId: currentUser.tenantId,
          terms_url: 'https://www.atlassian.com/legal/cloud-terms-of-service',
          two_factor: true,
          uid: currentUser.uid,
          updated_at: serverTimestamp(),
          vendor_name: 'Bitbucket',
          website_url: 'https://bitbucket.org',
        });
      } else {
        // If integration exists, get its ID
        integrationId = existingIntegration.docs[0].id;
      }
    }
    return integrationId;
  } catch (error) {
    showErrorToast({ message: 'Error setting up Bitbucket integration' });
    navigate('/integrations', { replace: true });
    throw error;
  }
};

export default {
  createBitbucketIntegration,
  exchangeCodeForBitbucketToken,
}
